import React, { useEffect, useState } from "react";
import {
  Card,
  List,
  Button,
  Typography,
  Modal,
  Form,
  Select,
  Space,
  Divider,
  Avatar,
  Tag,
  message,
  Spin,
  Alert,
} from "antd";
import { StarFilled, StarOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import usePerformanceReview from "Hooks/EmployeeHooks/usePerformanceReview";
const { Title, Text } = Typography;
const { Option } = Select;

const CheckReviews = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const employeeId = state?.employeeId;
  const employeeName = state?.employeeName;

  const {
    employee,
    reviews,
    skills,
    selectedSkills,
    setSelectedSkills,
    isButtonDisabled,
    modalVisible,
    setModalVisible,
    fetchAvailableSkills,
    addSkills,
    loading,
    error,
    countSkills,
    existingSkills,
  } = usePerformanceReview(employeeId, employeeName);

  const [form] = Form.useForm();
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (modalVisible && skills.length === 0) {
      const departmentId = state?.departmentId;
      setLoadingSkills(true);
      fetchAvailableSkills(departmentId)
        .catch((err) => {
          console.error("Failed to fetch skills:", err);
          message.error("Failed to load skills. Please try again.");
        })
        .finally(() => setLoadingSkills(false));
    }
  }, [modalVisible, skills.length, fetchAvailableSkills, existingSkills, form, setSelectedSkills]);

  const closeModal = () => {
    setModalVisible(false);
    form.resetFields();
    setSelectedSkills([]);

    navigate(-1); //  go back to previous page
  };

  const handleStartReview = () => {
    if (countSkills < 5) {
      message.warning("Please add at least 5 skills before starting review.");
      return;
    }

    navigate("/employeedashboard/performance-review/form", {
      state: {
        employeeId,
        employeeName: employeeName || employee?.employeeName,
      },
    });
  };

  const existingIds = existingSkills?.map((s) => s.skillId) || [];

  const handleModalSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();

      // Separate existing skills (numbers) from custom skills (strings)
      const skillIds = values.skills.filter((skill) => typeof skill === "number");
      const customSkills = values.skills.filter((skill) => typeof skill === "string");

      // Create payload matching API structure
      const payload = {
        employeeId: employeeId,
        skillIds: skillIds,
        customSkills: customSkills,
      };

      await addSkills(payload);
    } catch (error) {
      console.error("Error submitting skills:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
      }
      message.error("Failed to add skills. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => (dateString ? format(parseISO(dateString), "PP") : "N/A");

  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    for (let i = 0; i < fullStars; i++)
      stars.push(<StarFilled key={`full-${i}`} style={{ color: "#faad14" }} />);
    if (hasHalfStar)
      stars.push(<StarFilled key="half" style={{ color: "#faad14", opacity: 0.5 }} />);
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++)
      stars.push(<StarOutlined key={`empty-${i}`} style={{ color: "#faad14" }} />);
    return <Space>{stars}</Space>;
  };

  const getRatingColor = (ratingCode) => {
    switch (ratingCode) {
      case "FM":
        return "red";
      case "ME":
        return "orange";
      case "EE":
        return "green";
      case "AE":
        return "blue";
      default:
        return "gray";
    }
  };

  //  IMPROVED LOADING STATE
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            padding: "20px",
          }}
        >
          <Spin size="large" />
        </div>
      </DashboardLayout>
    );
  }

  //  IMPROVED ERROR STATE
  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: "20px" }}>
          <Card>
            <Alert
              type="error"
              message="Error Loading Performance Reviews"
              description={
                <div>
                  <p>{error}</p>
                  <p>Please check if the employee ID is correct and try again.</p>
                </div>
              }
              showIcon
            />
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <Button className="btn-primary" onClick={() => window.location.reload()}>
                Try Again
              </Button>
              <Button onClick={() => navigate("/manager-dashboard")}>Go to Dashboard</Button>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  //  CHECK IF EMPLOYEE DATA IS AVAILABLE
  if (!employee && !employeeName) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: "20px" }}>
          <Card>
            <Alert
              type="warning"
              message="Employee Information Missing"
              description="Please go back and select an employee to view performance reviews."
              showIcon
            />
            <Button
              className="btn-primary"
              style={{ marginTop: 16 }}
              onClick={() => navigate("/manager-dashboard")}
            >
              Go to Dashboard
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "20px" }}>
        <Card
          className="card-professional"
          title={`Performance Reviews: ${employee?.employeeName || employeeName}`}
          extra={
            <Button type="default" onClick={() => navigate(-1)}>
              Back
            </Button>
          }
        >
          {/* New Performance Review Section */}
          <div
            style={{
              marginBottom: "32px",
              padding: "24px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ffffff 0%, #f7fee7 100%)", // Brand Green Tint
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)", // Soft elevation
              border: "none",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Start New Performance Review
                </Title>
                {!employee?.isReviewAllowed && employee?.nextEligibleReviewDate ? (
                  <div style={{ marginTop: 8 }}>
                    <Text type="warning">
                      Next review available on{" "}
                      <strong>{formatDate(employee.nextEligibleReviewDate)}</strong>
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      Performance reviews can be conducted every 6 months
                    </Text>
                  </div>
                ) : employee?.isReviewAllowed ? (
                  <Text style={{ marginTop: 8, display: "block" }}>
                    Ready to start a new performance review
                  </Text>
                ) : null}
              </div>
              <Button
                className="btn-primary"
                size="large"
                onClick={handleStartReview}
                disabled={isButtonDisabled || !employee?.isReviewAllowed || countSkills < 5}
              >
                Start Review
              </Button>{" "}
            </div>
          </div>

          {/* Review History Section */}
          <Divider>
            <Title level={3} style={{ margin: 0 }}>
              Review History
            </Title>
          </Divider>

          {reviews && reviews.length > 0 ? (
            <List
              itemLayout="vertical"
              dataSource={reviews}
              renderItem={(review, index) => (
                <List.Item
                  key={review.id || index}
                  style={{
                    padding: "20px",
                    backgroundColor: "#fff",
                    border: "none", // Removes the box lines
                    borderRadius: "12px",
                    marginBottom: "16px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.04)", // "Un-boxed" depth
                    transition: "transform 0.2s ease",
                  }}
                  extra={
                    <Button
                      className="btn-primary"
                      onClick={() =>
                        navigate("/employeedashboard/performance-review/report", {
                          state: {
                            employeeId: employeeId,
                            reviewId: review.id,
                            reviewData: review,
                          },
                        })
                      }
                    >
                      View Full Report
                    </Button>
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          verticalAlign: "middle",
                          backgroundColor: "#0c313f", // Brand Navy
                          fontWeight: "bold",
                        }}
                      >
                        {reviews.length - index}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>
                          {formatDate(review.periodStart)} - {formatDate(review.periodEnd)}
                        </Text>
                        <Tag color={getRatingColor(review.performanceRatingCode)}>
                          {review.performanceRatingName || review.performanceRatingCode}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <div>
                          <Text strong>Overall Rating: </Text>
                          {renderRating(review.rating)}
                          <Text style={{ marginLeft: 8 }}>
                            (
                            {review.totalPerformanceScore
                              ? review.totalPerformanceScore.toFixed(1)
                              : "N/A"}
                            /100)
                          </Text>{" "}
                        </div>
                        <Text type="secondary">
                          Reviewed by {review.reviewerName} on {formatDate(review.createdOn)}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Card
              style={{
                textAlign: "center",
                backgroundColor: "#fafafa",
              }}
            >
              <Text type="secondary" style={{ fontSize: "16px" }}>
                No performance reviews found for this employee
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: "14px" }}>
                Start a new review to begin tracking performance
              </Text>
            </Card>
          )}

          {/* Skills Modal */}
          <Modal
            title={
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {countSkills > 0
                    ? `Add More Skills for Performance Review (Current: ${countSkills}/5)`
                    : "Add Skills for Performance Review"}
                </Title>
                <Text type="secondary">
                  {countSkills > 0
                    ? `This employee currently has ${countSkills} skill(s). Please add ${
                        5 - countSkills
                      } more skills to proceed.`
                    : "Select skills that will be used in the performance evaluation (minimum 5 required)"}
                </Text>
              </div>
            }
            open={modalVisible}
            onOk={handleModalSubmit}
            onCancel={closeModal}
            okText={
              countSkills > 0 ? "Add More Skills & Start Review" : "Add Skills & Start Review"
            }
            cancelText="Cancel"
            confirmLoading={isSubmitting}
            okButtonProps={{
              disabled: (existingSkills?.length || 0) + (selectedSkills?.length || 0) < 5,
            }}
            width={600}
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="skills"
                label="Select Skills (Minimum 5 required)"
                rules={[
                  {
                    validator: (_, value) => {
                      const existingCount = existingSkills?.length || 0;
                      const newCount = Array.isArray(value) ? value.length : 0;
                      const total = existingCount + newCount;

                      if (total >= 5) return Promise.resolve();

                      return Promise.reject(
                        new Error(`Please add at least ${5 - existingCount} more skill(s)`)
                      );
                    },
                  },
                ]}
              >
                <Select
                  mode="tags"
                  placeholder={
                    loadingSkills ? "Loading skills..." : "Select skills or type to add new skills"
                  }
                  loading={loadingSkills}
                  value={selectedSkills}
                  onChange={(value) => {
                    setSelectedSkills(value);
                    form.setFieldsValue({ skills: value });
                  }}
                  tokenSeparators={[","]}
                  size="large"
                >
                  {skills
                    .filter((skill) => !existingIds.includes(skill.id))
                    .map((skill) => (
                      <Option key={skill.id} value={skill.id}>
                        {skill.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
              {existingSkills && existingSkills.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Title level={5}>Current Skills:</Title>
                  <div style={{ marginBottom: 16 }}>
                    {existingSkills.map((skill, index) => (
                      <Tag
                        key={index}
                        style={{
                          marginBottom: 8,
                          padding: "4px 12px",
                          borderRadius: "4px",
                          background: "#f1f5f9", // Light Slate
                          border: "none",
                          color: "#475569",
                          fontWeight: 500,
                        }}
                      >
                        {skill.skillName || skill.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              <Alert
                message="Skills Information"
                description="Select at least 5 skills for the performance review. You can choose from the list or type to add new custom skills."
                type="info"
                showIcon
              />
            </Form>
          </Modal>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CheckReviews;
